<template>
  <with-loader id="answer-end-editor" class="flex flex-col" :loading="isLoading" use-as-overlay>
    <span class="font-bold text-gray-dark text-xl">Message transition</span>
    <span class="text-gray-dark mt-4">Choose what happens after the preceding message</span>

    <div class="flex flex-col text-gray-dark">
      <base-radio v-for="(answerEnd, i) of answerEndList"
                  :key="i"
                  :id="`answerEnd${i}`"
                  :value="answerEnd.type"
                  :name="'answerEnd'"
                  class="mt-4 ml-2"
                  v-model="selectedAnswerEndType"
      >
        <div class="flex items-center">
          <span class="ml-1">{{ answerEnd.text }}</span>
          <img :src="answerEnd.icon"
               :alt="`${answerEnd.text} icon`"
               :height="answerEnd.answerEndIconHeight || '16px'"
               :class="answerEnd.class"
               class="ml-2"/>
        </div>
      </base-radio>
    </div>

    <div v-if="isContinueAnswer">
      <div class="mt-5 text-gray-dark text-sm flex flex-col gap-2">
        <div class="text-gray-dark text-sm" v-html="answerEndings.continueAnswer.description" />
        <queryable-select :value="selectedAnswerId"
                          :key="selectedAnswerId"
                          :options="answers"
                          text-attribute="name"
                          value-attribute="id"
                          class="w-full border-gray h-10"
                          should-focus-on-mount
                          @input="selectedAnswerId = $event"
        />
        <router-link v-if="selectedAnswerId && !isCurrentAnswer"
                     :to="{name: ROUTE_NAMES.answers, params: { botId: botId, answerId: selectedAnswerId}}"
                     class="flex items-center gap-1"
        >
          <span class="hover:underline">View Answer</span>
          <vs-icon icon="call_made" size="1.2rem"/>
        </router-link>
      </div>
    </div>
    <div v-else class="mt-5 text-gray-dark text-sm" v-html="selectedAnswerEnd.description" />
  </with-loader>
</template>

<script>
import tailwind from '../../../tailwind.config'
import WithLoader from '@/components/common/WithLoader'
import QueryableSelect from '@/components/common/QueryableSelect.vue'
import continueFindAnswerIcon from '@/assets/images/icons/continue_find_answer_icon.svg'
import findIntentIcon from '@/assets/images/icons/find_intent_icon.svg'
import callOperatorIcon from '@/assets/images/icons/call_operator_icon.svg'
import waitInputThenIdleIcon from '@/assets/images/icons/wait_input_then_idle_icon.svg'
import continueAnswerIcon from '@/assets/images/icons/continue_answer_icon.svg'
import continueBlockIcon from '@/assets/images/icons/continue_block_icon.svg'
import continueConditionIcon from '@/assets/images/icons/continue_condition_icon.svg'
import chatBubbleModule from '@/store/modules/chat-bubble.module'
import templateModule from '@/store/modules/template.module'
import answerController from '@/controllers/answer.controller'
import templateController from '@/controllers/template.controller'
import { ANSWER_END } from '@/utils/bot-json.constants'
import { ITEM_IDS } from '@/utils/editor.constants'
import toaster from '@/utils/toaster'
import stateTreeUtils from '@/utils/state-tree.utils'
import conversationModule from '@/store/modules/conversation.module'
import contentUtils from '@/utils/content.utils'
import editorUtils from '@/utils/editor.utils'
import isEmpty from 'ramda/src/isEmpty'
import { PREVIEW_ITEM } from '@/store/constants'
import answerModule from '@/store/modules/answer.module'
import answerService from '@/services/amio/answer.service'
import templateService from '@/services/amio/template.service'
import { ROUTE_NAMES } from '@/router'
import { NODE_TYPES } from '@/utils/state-tree.generator'

export default {
  name: 'AnswerEndEditor',

  components: { WithLoader, QueryableSelect },

  props: {
    botId: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      ROUTE_NAMES,
      isLoading: false,
      selectedAnswerEndType: '',
      cachedAnswerEndType: null,
      answers: [],
      answerEndings: {
        callOperator: {
          text: 'Call Operator',
          type: ANSWER_END.IDLE,
          icon: callOperatorIcon,
          description: 'The virtual assistant will <span class="font-bold">Call Operator</span> and hand over the customer request to a customer support agent.',
          class: 'mb-px'
        },
        waitForInputThenIdle: {
          text: 'Wait for Input ➔ Call Operator',
          type: ANSWER_END.WAIT_INPUT_THEN_IDLE,
          icon: waitInputThenIdleIcon,
          answerEndIconHeight: '21px',
          description: 'First the virtual assistant will wait for the customer\'s reply. Then it will hand over the request to a customer support agent.'
        },
        findIntent: {
          text: 'Find Answer',
          type: ANSWER_END.FIND_INTENT,
          icon: findIntentIcon,
          description: 'The virtual assistant will wait for customer input then <span class="font-bold">Find Answer</span> to reply with an appropriate answer.',
          class: 'mb-px'
        },
        continueCondition: {
          text: 'Exit the Condition',
          type: ANSWER_END.CONTINUE_CONDITION,
          icon: continueConditionIcon,
          description: 'The virtual assistant will <span class="font-bold">Exit the Condition</span> and continue the conversation flow to messages defined outside of the condition.',
          class: 'mb-px'
        },
        continueTemplate: {
          text: 'Exit the Block',
          type: ANSWER_END.CONTINUE_TEMPLATE,
          icon: continueBlockIcon,
          description: 'The virtual assistant will <span class="font-bold">Exit the Block</span> and continue the conversation flow to messages defined outside of the block.',
          class: 'mb-px'
        },
        continueAnswer: {
          text: 'Continue to Answer',
          type: ANSWER_END.CONTINUE_ANSWER,
          icon: continueAnswerIcon,
          description: 'The virtual assistant will <span class="font-bold">Continue to Answer</span>:',
          class: 'mb-px'
        },
        continueOrFindIntent: {
          text: 'Continue this Answer or Find Answer',
          type: ANSWER_END.CONTINUE_OR_FIND_INTENT,
          icon: continueFindAnswerIcon,
          description: 'AI first decides whether the customer keeps talking about this answer\'s topic. ' +
            'If yes, this answer replies again; if not, the virtual assistant will <span class="font-bold">Find Answer</span>.',
          class: 'mb-px'
        }
      }
    }
  },

  watch: {
    async selectedAnswerEndType(newType, oldType) {
      if (newType === ANSWER_END.CONTINUE_ANSWER) {
        await this.loadAnswers()
      }

      if (!oldType) return
      this.updateAnswerEnd(newType, oldType)
    }
  },

  computed: {
    bubbleSelected: chatBubbleModule.bubbleSelected,
    stateTree: chatBubbleModule.stateTree,
    template: templateModule.template,

    answerId() {
      return this.$route.params.answerId
    },

    isCurrentAnswer() {
      return this.answerId && this.answerId === this.selectedAnswerId
    },

    selectedAnswerId: {
      get() {
        return this.bubbleSelected.answerId || null
      },
      set(answerId) {
        this.changeAnswer(answerId)
      }
    },

    focusColor() {
      return tailwind.theme.colors.focus
    },

    answerEndList() {
      const { isCondition, isPostback } = this.bubbleSelected
      const { callOperator, waitForInputThenIdle, findIntent, continueCondition, continueTemplate, continueAnswer, continueOrFindIntent } = this.answerEndings
      if (this.isEmptyAnswer) return [findIntent, callOperator]

      if (this.isDanglingState) return [callOperator, continueAnswer]

      const answerEnds = [callOperator, waitForInputThenIdle, findIntent, continueAnswer]
      if (this.canContinueOrFindIntent) answerEnds.push(continueOrFindIntent)
      if (isCondition && !isPostback) answerEnds.push(continueCondition)
      if (templateModule.isTemplateEditor() && !isCondition && !this.hasPreviousBubbleQuickReply) answerEnds.push(continueTemplate)

      return answerEnds
    },

    selectedAnswerEnd() {
      return this.answerEndList.find(answerEnd => answerEnd.type === this.bubbleSelected.value) || {}
    },

    hasPreviousBubbleQuickReply() {
      if (!this.node?.parent?.node) return false

      const action = conversationModule.getAction(this.node.parent.node.stateId, this.node.parent.node.actionId)
      const content = contentUtils.getQuickReplies(action)
      return !isEmpty(content)
    },

    isEmptyAnswer() {
      return this.bubbleSelected.type === PREVIEW_ITEM.ANSWER_END && answerModule.isEmptyAnswer()
    },

    isDanglingState() {
      if (this.bubbleSelected.type !== PREVIEW_ITEM.ANSWER_END || !this.node) return false
      if (!stateTreeUtils.nodeIsQuickReply(this.node) && !stateTreeUtils.nodeIsPostback(this.node)) return false

      return [NODE_TYPES.CONTINUE_ANSWER, NODE_TYPES.IDLE].includes(this.node.type)
    },

    node() {
      return stateTreeUtils.findNode(this.stateTree, this.bubbleSelected)
    },

    isContinueAnswer() {
      return this.selectedAnswerEndType === ANSWER_END.CONTINUE_ANSWER
    },

    // PRO-367 / PRO-673: works on any bot type, any answer and any last bubble type (email bots were excluded
    // until the engine started refreshing context.customerRequest per message), but it needs a state that owns
    // a single answer-end transition: condition states keep one transition per case (the pair would read as a
    // phantom case) and the template editor has no mapping for it yet.
    canContinueOrFindIntent() {
      if (templateModule.isTemplateEditor()) return false

      return !conversationModule.isConditionState(this.resolveAnswerEndStateId())
    }
  },

  methods: {
    async loadAnswers() {
      this.answers = await answerService.list(this.botId)
    },

    async changeAnswer(answerId) {
      await this.updateAnswerEnd(ANSWER_END.CONTINUE_ANSWER, this.cachedAnswerEndType, answerId)
    },

    getStateIdForAnswerEnd(stateId) {
      return templateModule.isTemplateEditor() ?
        templateController.getStateIdForAnswerEnd(stateId, this.bubbleSelected.isCondition, this.template.id) :
        answerController.getStateIdForAnswerEnd(stateId, this.bubbleSelected.isCondition)
    },

    resolveAnswerEndStateId() {
      if (this.isEmptyAnswer || !this.node) {
        return this.bubbleSelected.stateId
      }

      if (stateTreeUtils.nodeIsQuickReply(this.node) || stateTreeUtils.nodeIsPostback(this.node) || this.node.isPostback) {
        return this.node.parent.node.stateId
      }

      return this.bubbleSelected.stateId
    },

    async updateAnswerEnd(type, oldType, answerId) {
      if (type === ANSWER_END.CONTINUE_ANSWER && !answerId && oldType) {
        this.cachedAnswerEndType = oldType
        // retaining the existing answer end preview item until the user selects an answer to continue to
        chatBubbleModule.selectAnswerEnd(this.bubbleSelected.stateId, oldType, false, this.bubbleSelected.isCondition, this.bubbleSelected.conditionEndStateId, this.bubbleSelected.isPostback, this.bubbleSelected.isQuickReply)
        return
      }

      this.cachedAnswerEndType = null
      const stateId = this.resolveAnswerEndStateId()
      const transitionIndex = this.isDanglingState ? this.node.parent.transition.index : -1
      if(templateModule.isTemplateEditor()) {
        this.isLoading = true
        try {
          if (this.template.id !== ITEM_IDS.NEW && oldType === ANSWER_END.CONTINUE_TEMPLATE) {
            const wasPreviewChanged = editorUtils.detectPreviewChange(false)
            if (!wasPreviewChanged) await this.processReferencedConversations(this.template)
          }
          templateController.updateAnswerEnd(stateId, type, this.bubbleSelected, templateModule.getTemplateId(), answerId, transitionIndex)
        } catch(err) {
          toaster.error(this.resolveErrMessage(err.message), err)

          // vs-radio doesn't provide a way to ignore the click and return to previously selected value
          // so the only way to go back is to actually change the value back and forth
          // (but of course the real reason for the timeout is that it looks cool)
          chatBubbleModule.selectAnswerEnd(stateId, type, false, this.bubbleSelected.isCondition, this.bubbleSelected.conditionEndStateId, this.bubbleSelected.isPostback, this.bubbleSelected.isQuickReply, false, answerId)
          setTimeout(() => {
            chatBubbleModule.selectAnswerEnd(stateId, oldType, false, this.bubbleSelected.isCondition, this.bubbleSelected.conditionEndStateId, this.bubbleSelected.isPostback, this.bubbleSelected.isQuickReply)
          }, 100)
          return
        } finally {
          this.isLoading = false
        }
      } else {
        answerController.updateAnswerEnd(stateId, type, this.bubbleSelected, answerId, transitionIndex)
      }

      chatBubbleModule.selectAnswerEnd(this.bubbleSelected.stateId, type, false, this.bubbleSelected.isCondition, this.bubbleSelected.conditionEndStateId, this.bubbleSelected.isPostback, this.bubbleSelected.isQuickReply, false, answerId)
    },

    async processReferencedConversations(template) {
      const { answers, events, answersHashes, eventsHashes } = await templateService.getReferencedConversations(this.botId, template.id)
      await Promise.all([
        conversationModule.setTemplatesHashes({ [template.id]: conversationModule.getRootJsonHash() }),
        conversationModule.setAnswers(answers),
        conversationModule.setAnswersHashes(answersHashes),
        conversationModule.setEvents(events),
        conversationModule.setEventsHashes(eventsHashes)
      ])
    },
    
    resolveErrMessage(message) {
      const isAnswer = message.includes('answers:')
      const itemType = isAnswer ? 'Answer' : 'Event'
      const start = message.indexOf(isAnswer ? 'answers:' : 'events:') + itemType.length + 2
      const itemId = message.substring(start, message.length).trim()
      return `The Answer End cannot be changed because the Block is used in this
              <a target="_blank" href="/bots/${this.botId}/${itemType.toLowerCase()}s/${itemId}">${itemType}</a>
              that has follow-on messages.`
    }
  },

  mounted() {
    this.selectedAnswerEndType = this.bubbleSelected.value
  }
}
</script>
